import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApps } from '@/contexts/AppsContext';
import { authApi, schemaApi, serviceCatalogApi, apiKeysApi } from '@/services/api';
import SchemaEditor from '@/components/schema/SchemaEditor';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  RefreshCw,
  Check,
  Users,
  UserCircle,
  Bot,
  Key,
  FileText,
  Loader2,
  Copy,
  Shield,
  ExternalLink,
} from 'lucide-react';

const STEPS = [
  { label: 'Terms', icon: Shield },
  { label: 'Describe', icon: FileText },
  { label: 'Ownership', icon: Users },
  { label: 'Schema', icon: Sparkles },
  { label: 'API Key', icon: Key },
  { label: 'Done', icon: Check },
];

const AUTHORSHIP_OPTIONS = [
  {
    value: 'multi',
    label: 'Multi-Author',
    icon: Users,
    description: 'Multiple people contribute content. Each person owns their data and controls access.',
    example: 'e.g., Memily, family journal',
  },
  {
    value: 'single',
    label: 'Single Author',
    icon: UserCircle,
    description: 'One person or team authors all content.',
    example: 'e.g., knowledge base, documentation',
  },
  {
    value: 'app',
    label: 'App-as-Author',
    icon: Bot,
    description: 'The system ingests content automatically. No human authorship.',
    example: 'e.g., web scraper, RSS aggregator',
  },
];

// Step indicator bar
function StepIndicator({ currentStep }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEPS.map((step, idx) => {
        const isActive = idx === currentStep;
        const isDone = idx < currentStep;
        const Icon = step.icon;

        return (
          <React.Fragment key={step.label}>
            {idx > 0 && (
              <div className={`w-8 h-px ${isDone ? 'bg-primary' : 'bg-muted'}`} />
            )}
            <div className="flex items-center gap-1.5">
              <div
                className={`flex items-center justify-center w-7 h-7 rounded-full text-xs transition-colors ${
                  isDone
                    ? 'bg-primary text-primary-foreground'
                    : isActive
                    ? 'bg-primary/20 text-primary border border-primary/50'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {isDone ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
              </div>
              <span
                className={`text-xs font-medium ${
                  isActive ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {step.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function CreateAppWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { apps, currentApp, createApp } = useApps();
  const isFirstApp = apps.length === 0;

  // Read initial step from URL params (for deep linking from sidebar)
  const requestedStep = parseInt(searchParams.get('step') || '0', 10);
  const [step, setStep] = useState(Math.max(0, Math.min(requestedStep, 5)));

  // Detect if we're re-setting up an existing app (e.g., schema was cleared)
  const isExistingAppSetup = !!currentApp && step >= 3;

  // Sync step when URL params change (sidebar click)
  useEffect(() => {
    const urlStep = parseInt(searchParams.get('step') || '0', 10);
    if (urlStep >= 0 && urlStep <= 5) {
      setStep(urlStep);
    }
  }, [searchParams]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [authorshipModel, setAuthorshipModel] = useState('multi');
  const [schema, setSchema] = useState(null);
  const [services, setServices] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [createdApp, setCreatedApp] = useState(null);
  const [keyName, setKeyName] = useState('');
  const [keyLoading, setKeyLoading] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  // Fetch service catalog on mount
  useEffect(() => {
    serviceCatalogApi.list()
      .then((res) => {
        const data = res.data?.data;
        setServices(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        // Service catalog not available — SchemaEditor will fall back to legacy mode
      });
  }, []);

  // Step 0 → 1 (Terms)
  const canProceedTerms = termsAccepted && privacyAccepted;
  const [acceptingTerms, setAcceptingTerms] = useState(false);

  async function handleAcceptTerms() {
    setError('');
    setAcceptingTerms(true);
    try {
      await authApi.acceptTerms({
        termsVersion: '2026-03-22',
        privacyVersion: '2026-03-22',
      });
      setStep(1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record terms acceptance');
    } finally {
      setAcceptingTerms(false);
    }
  }

  // Step 1 → 2 (Describe)
  const canProceedStep1 = name.trim().length > 0;

  // Step 2 → 3 (generate schema)
  async function handleGenerateSchema() {
    setError('');
    setGenerating(true);
    try {
      const res = await schemaApi.generate({
        description: `App Name: ${name}\n\n${description}`,
        authorship_model: authorshipModel,
      });
      setSchema(res.data?.data);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Schema generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  }

  // Regenerate schema
  async function handleRegenerate() {
    setError('');
    setGenerating(true);
    try {
      const res = await schemaApi.generate({
        description: `App Name: ${name}\n\n${description}`,
        authorship_model: authorshipModel,
      });
      setSchema(res.data?.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Schema generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  }

  // Create app with schema (or save schema to existing app)
  async function handleCreateApp() {
    setError('');
    setCreating(true);
    try {
      if (isExistingAppSetup) {
        // Existing app — save schema, don't create a new app
        await schemaApi.update(currentApp.appId, schema);
        setCreatedApp(currentApp);
      } else {
        // New app — create and save schema
        const newApp = await createApp({
          name,
          description,
          schema,
        });
        setCreatedApp(newApp);
      }
      setStep(4); // API Key step
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create app');
    } finally {
      setCreating(false);
    }
  }

  async function handleCreateKey(e) {
    e.preventDefault();
    if (!keyName.trim()) return;
    setError('');
    setKeyLoading(true);
    try {
      const res = await apiKeysApi.create(createdApp.appId, { name: keyName });
      setCreatedKey(res.data.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to create API key');
    } finally {
      setKeyLoading(false);
    }
  }

  async function handleCopyKey() {
    if (createdKey?.plainKey) {
      await navigator.clipboard.writeText(createdKey.plainKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Back Button — hidden for first-time users with no apps */}
      {!isFirstApp && (
        <Button variant="ghost" onClick={() => navigate('/apps')} className="mb-2">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Apps
        </Button>
      )}

      <StepIndicator currentStep={step} />

      {/* Error display */}
      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* ==================== STEP 0: TERMS & PRIVACY ==================== */}
      {step === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Terms of Service & Privacy Policy</CardTitle>
            <CardDescription>
              Please review and accept our terms before getting started.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="rounded-lg border p-4 space-y-3">
              <h4 className="font-medium text-sm">Key Terms</h4>
              <ul className="text-xs text-muted-foreground space-y-1.5">
                <li>• <strong>Your data belongs to you.</strong> We do not claim ownership of any content you ingest.</li>
                <li>• <strong>We do not train on your data.</strong> Your content is never used to train AI models without your explicit written consent.</li>
                <li>• Content is processed by AI providers (Anthropic, Google) via their APIs. These providers do not train on API inputs.</li>
                <li>• We do not sell, rent, or share your data with third parties for commercial purposes.</li>
                <li>• The Service is provided "as is" without warranties.</li>
              </ul>
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-xs text-primary hover:underline">
                Read full Terms of Service <ExternalLink className="ml-1 h-3 w-3" />
              </a>
            </div>

            <div className="rounded-lg border p-4 space-y-3">
              <h4 className="font-medium text-sm">Privacy Highlights</h4>
              <ul className="text-xs text-muted-foreground space-y-1.5">
                <li>• Your data is encrypted in transit (TLS) and at rest (AES-256).</li>
                <li>• Each app gets isolated storage — other tenants cannot access it.</li>
                <li>• You can request data export or deletion at any time.</li>
                <li>• We collect minimal usage metrics to operate and improve the Service.</li>
              </ul>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-xs text-primary hover:underline">
                Read full Privacy Policy <ExternalLink className="ml-1 h-3 w-3" />
              </a>
            </div>

            <div className="space-y-3 pt-2">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="h-4 w-4 rounded border-border accent-primary"
                />
                <span className="text-sm">I have read and agree to the <strong>Terms of Service</strong></span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="h-4 w-4 rounded border-border accent-primary"
                />
                <span className="text-sm">I have read and agree to the <strong>Privacy Policy</strong></span>
              </label>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleAcceptTerms} disabled={!canProceedTerms || acceptingTerms}>
                {acceptingTerms ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ==================== STEP 1: DESCRIBE ==================== */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Describe Your App</CardTitle>
            <CardDescription>
              Tell us what your app does. The more detail you provide, the better the AI can design your service subscriptions and graph schema.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">App Name *</Label>
              <Input
                id="name"
                placeholder="My Awesome App"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                placeholder="Describe what your app does, what kind of data it will process, and what relationships matter. For example: 'A personal memory system that ingests journal entries, photos, and voice memos. It should track people, places, emotions, and life themes. Multiple family members will contribute their own content.'"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[140px]"
              />
              <p className="text-xs text-muted-foreground">
                This description drives AI schema generation. Include data types, relationships, and use cases.
              </p>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!canProceedStep1}>
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ==================== STEP 2: OWNERSHIP ==================== */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Ownership Model</CardTitle>
            <CardDescription>
              This is the one decision that doesn't change. Who creates the content?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-3">
              {AUTHORSHIP_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const selected = authorshipModel === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setAuthorshipModel(opt.value)}
                    className={`flex items-start gap-4 p-4 rounded-lg border text-left transition-colors ${
                      selected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground/30'
                    }`}
                  >
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${
                        selected ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{opt.label}</span>
                        {selected && <Badge className="text-[10px]">Selected</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{opt.description}</p>
                      <p className="text-[10px] text-muted-foreground/70 mt-0.5">{opt.example}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button onClick={handleGenerateSchema} disabled={generating}>
                {generating ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Generating Schema...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate Schema
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ==================== STEP 3: REVIEW SCHEMA ==================== */}
      {step === 3 && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Review Schema</CardTitle>
                  <CardDescription>
                    The AI selected platform services for your app. Toggle services, review the computed graph schema, and adjust the pipeline.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRegenerate}
                  disabled={generating}
                >
                  {generating ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  <span className="ml-1.5">Regenerate</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <SchemaEditor
                schema={schema}
                onChange={setSchema}
                services={services}
              />
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button onClick={handleCreateApp} disabled={creating}>
              {creating ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  {isExistingAppSetup ? 'Saving Schema...' : 'Creating App...'}
                </>
              ) : (
                <>
                  {isExistingAppSetup ? 'Save Schema' : 'Create App'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* ==================== STEP 4: API KEY ==================== */}
      {step === 4 && createdApp && (
        <Card>
          <CardHeader>
            <CardTitle>Create Your First API Key</CardTitle>
            <CardDescription>
              You need an API key to send data to your app. You can create more keys later from the API Keys tab.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!createdKey ? (
              <form onSubmit={handleCreateKey} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="key-name">Key Name</Label>
                  <Input
                    id="key-name"
                    placeholder="e.g. Production API Key"
                    value={keyName}
                    onChange={(e) => setKeyName(e.target.value)}
                    disabled={keyLoading}
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground">
                    A descriptive name to identify this key later.
                  </p>
                </div>
                <Button type="submit" className="w-full" disabled={keyLoading || !keyName.trim()}>
                  {keyLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Key className="mr-2 h-4 w-4" />
                      Create API Key
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-green-500">
                  <Check className="h-4 w-4" />
                  API Key Created
                </div>
                <div className="space-y-1.5">
                  <Label>Your API Key</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-muted px-3 py-2 rounded text-xs font-mono break-all select-all">
                      {createdKey.plainKey}
                    </code>
                    <Button variant="outline" size="icon" onClick={handleCopyKey} className="flex-shrink-0 h-9 w-9">
                      {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="rounded-md bg-yellow-500/10 px-3 py-2.5 text-xs text-yellow-600 dark:text-yellow-400">
                  <strong>Save this key now</strong> — you won't be able to see it again.
                </div>
                <Button className="w-full" onClick={() => setStep(5)}>
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ==================== STEP 5: DONE ==================== */}
      {step === 5 && createdApp && (
        <Card>
          <CardContent className="py-12 text-center space-y-6">
            <div className="flex items-center justify-center">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-500/15">
                <Check className="h-8 w-8 text-green-500" />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-semibold">{createdApp.name} is Ready</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Schema generated and API key created. Your app is fully set up.
              </p>
            </div>
            <Button size="lg" onClick={() => navigate(`/apps/${createdApp.appId}`)}>
              Go to App
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
